import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import AdminHeader from '../../components/admin/AdminHeader'
import AdminSidebar from '../../components/admin/AdminSidebar'
import { API_BASE_URL } from '../../config/api'

interface DashboardStats {
  totalBanners: number
  totalVideos: number
  totalEvents: number
  totalNewsletterSubscribers: number
  activeBanners: number
  activeVideos: number
  upcomingEvents: number
  pastEvents: number
}

const AdminDashboard = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [stats, setStats] = useState<DashboardStats>({
    totalBanners: 0,
    totalVideos: 0,
    totalEvents: 0,
    totalNewsletterSubscribers: 0,
    activeBanners: 0,
    activeVideos: 0,
    upcomingEvents: 0,
    pastEvents: 0
  })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')


  // Fetch all dashboard statistics
  const fetchDashboardStats = async () => {
    setLoading(true)
    setError('')
    
    try {
      // Fetch all data in parallel
      const [heroResponse, eventsResponse, newsletterResponse] = await Promise.all([
        fetch(`${API_BASE_URL}hero_list.php`),
        fetch(`${API_BASE_URL}event_list.php`),
        fetch(`${API_BASE_URL}newsletter_list.php`)
      ])

      const heroResult = await heroResponse.json()
      const eventsResult = await eventsResponse.json()
      const newsletterResult = await newsletterResponse.json()

      // Process hero items (banners and videos)
      let totalBanners = 0
      let totalVideos = 0
      let activeBanners = 0
      let activeVideos = 0

      if (heroResult.status && heroResult.data) {
        heroResult.data.forEach((item: any) => {
          if (item.type === 'image') {
            totalBanners++
            if ((item.is_active ?? 1) === 1) {
              activeBanners++
            }
          } else if (item.type === 'video') {
            totalVideos++
            if ((item.is_active ?? 1) === 1) {
              activeVideos++
            }
          }
        })
      }

      // Process events
      let totalEvents = 0
      let upcomingEvents = 0
      let pastEvents = 0

      if (eventsResult.status && eventsResult.data) {
        totalEvents = eventsResult.data.length
        eventsResult.data.forEach((event: any) => {
          if (event.status === 'upcoming') {
            upcomingEvents++
          } else if (event.status === 'past') {
            pastEvents++
          }
        })
      }

      // Process newsletter subscribers
      let totalNewsletterSubscribers = 0
      if (newsletterResult.status && newsletterResult.data) {
        totalNewsletterSubscribers = newsletterResult.data.length
      }

      setStats({
        totalBanners,
        totalVideos,
        totalEvents,
        totalNewsletterSubscribers,
        activeBanners,
        activeVideos,
        upcomingEvents,
        pastEvents
      })
    } catch (err) {
      setError('Failed to load dashboard statistics. Please try again.')
      console.error('Error fetching dashboard stats:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchDashboardStats()
  }, [])

  return (
    <div className="min-h-screen bg-gray-900">
      <AdminHeader onMenuToggle={() => setSidebarOpen(!sidebarOpen)} />
      <div className="flex">
        <AdminSidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
        <main className="flex-1 md:ml-64 p-4 md:p-6">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-6">
              <h1 className="text-3xl font-bold text-white">Dashboard</h1>
              <button
                onClick={fetchDashboardStats}
                className="bg-primary-light hover:bg-primary-dark text-white px-6 py-3 rounded-lg font-semibold transition-colors duration-300 flex items-center space-x-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                <span>Refresh</span>
              </button>
            </div>

            {/* Error Message */}
            {error && (
              <div className="mb-6 p-4 bg-red-500/20 border border-red-500 rounded-lg">
                <p className="text-red-400">{error}</p>
              </div>
            )}

            {/* Stats Cards */}
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="bg-gray-800 rounded-lg p-6 border border-gray-700 animate-pulse">
                    <div className="h-20 bg-gray-700 rounded"></div>
                  </div>
                ))}
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                  <div className="bg-gray-800 rounded-lg p-6 border border-gray-700 hover:shadow-lg transition-shadow">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-gray-400 text-sm mb-1">Total Banners</p>
                        <p className="text-3xl font-bold text-white">{stats.totalBanners}</p>
                        <p className="text-xs text-green-400 mt-1">{stats.activeBanners} active</p>
                      </div>
                      <div className="bg-primary-light/20 p-3 rounded-lg">
                        <svg className="w-6 h-6 text-primary-light" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gray-800 rounded-lg p-6 border border-gray-700 hover:shadow-lg transition-shadow">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-gray-400 text-sm mb-1">Total Videos</p>
                        <p className="text-3xl font-bold text-white">{stats.totalVideos}</p>
                        <p className="text-xs text-green-400 mt-1">{stats.activeVideos} active</p>
                      </div>
                      <div className="bg-purple-500/20 p-3 rounded-lg">
                        <svg className="w-6 h-6 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gray-800 rounded-lg p-6 border border-gray-700 hover:shadow-lg transition-shadow">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-gray-400 text-sm mb-1">Total Events</p>
                        <p className="text-3xl font-bold text-white">{stats.totalEvents}</p>
                        <p className="text-xs text-blue-400 mt-1">{stats.upcomingEvents} upcoming, {stats.pastEvents} past</p>
                      </div>
                      <div className="bg-blue-500/20 p-3 rounded-lg">
                        <svg className="w-6 h-6 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gray-800 rounded-lg p-6 border border-gray-700 hover:shadow-lg transition-shadow">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-gray-400 text-sm mb-1">Newsletter Subscribers</p>
                        <p className="text-3xl font-bold text-white">{stats.totalNewsletterSubscribers}</p>
                        <p className="text-xs text-green-400 mt-1">All active</p>
                      </div>
                      <div className="bg-green-500/20 p-3 rounded-lg">
                        <svg className="w-6 h-6 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Additional Stats */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                  <div className="bg-gradient-to-br from-primary-dark to-primary-light rounded-lg p-6 border border-gray-700">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-white/80 text-sm mb-1">Header Home Banner</p>
                        <p className="text-3xl font-bold text-white">{stats.totalBanners + stats.totalVideos}</p>
                      </div>
                      <div className="bg-white/20 p-3 rounded-lg">
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 4v16M17 4v16M3 8h4m10 0h4M3 12h18M3 16h4m10 0h4M4 20h16a1 1 0 001-1V5a1 1 0 00-1-1H4a1 1 0 00-1 1v14a1 1 0 001 1z" />
                        </svg>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-blue-600 to-blue-400 rounded-lg p-6 border border-gray-700">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-white/80 text-sm mb-1">Upcoming Events</p>
                        <p className="text-3xl font-bold text-white">{stats.upcomingEvents}</p>
                      </div>
                      <div className="bg-white/20 p-3 rounded-lg">
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-green-600 to-green-400 rounded-lg p-6 border border-gray-700">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-white/80 text-sm mb-1">Active Content</p>
                        <p className="text-3xl font-bold text-white">{stats.activeBanners + stats.activeVideos}</p>
                        <p className="text-xs text-white/80 mt-1">Banners & Videos</p>
                      </div>
                      <div className="bg-white/20 p-3 rounded-lg">
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* Quick Actions */}
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
              <h2 className="text-xl font-bold text-white mb-4">Quick Actions</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
                <Link
                  to="/admin/hero-management"
                  className="bg-primary-light hover:bg-primary-dark text-white px-6 py-3 rounded-lg font-semibold transition-colors duration-300 text-left flex items-center space-x-3"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  <span>Add New Banner</span>
                </Link>
                <Link
                  to="/admin/hero-management"
                  className="bg-primary-light hover:bg-primary-dark text-white px-6 py-3 rounded-lg font-semibold transition-colors duration-300 text-left flex items-center space-x-3"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  <span>Upload Video</span>
                </Link>
                <Link
                  to="/admin/hero-management"
                  className="bg-primary-light hover:bg-primary-dark text-white px-6 py-3 rounded-lg font-semibold transition-colors duration-300 text-left flex items-center space-x-3"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 4v16M17 4v16M3 8h4m10 0h4M3 12h18M3 16h4m10 0h4M4 20h16a1 1 0 001-1V5a1 1 0 00-1-1H4a1 1 0 00-1 1v14a1 1 0 001 1z" />
                  </svg>
                  <span>Header Home Banner</span>
                </Link>
                <Link
                  to="/admin/event-management"
                  className="bg-primary-light hover:bg-primary-dark text-white px-6 py-3 rounded-lg font-semibold transition-colors duration-300 text-left flex items-center space-x-3"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <span>Event Management</span>
                </Link>
                <Link
                  to="/admin/about-us-management"
                  className="bg-primary-light hover:bg-primary-dark text-white px-6 py-3 rounded-lg font-semibold transition-colors duration-300 text-left flex items-center space-x-3"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>About Us</span>
                </Link>
                <Link
                  to="/admin/newsletter-management"
                  className="bg-primary-light hover:bg-primary-dark text-white px-6 py-3 rounded-lg font-semibold transition-colors duration-300 text-left flex items-center space-x-3"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <span>Newsletter</span>
                </Link>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  )
}

export default AdminDashboard

